import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';

/**
 * Fallback success page. The checkout modal handles the synchronous response
 * inline, so this page is only reached if someone lands here directly (refresh
 * or a stale bookmark). We look up the purchase status and route accordingly.
 */
export default function PurchaseSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const purchaseId = params.get('id');
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    if (!purchaseId) {
      setStatus('missing_id');
      return;
    }
    if (!localStorage.getItem('learnable_user')) {
      setStatus('guest');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`/api/me/purchases/${purchaseId}`, {
          credentials: 'include',
        });
        const data = await res.json();
        const s = data.purchase?.status;
        if (s === 'completed') {
          setStatus('completed');
          setTimeout(() => navigate('/dashboard'), 800);
        } else if (s === 'failed') {
          setStatus('failed');
        } else {
          setStatus('unknown');
        }
      } catch {
        setStatus('unknown');
      }
    })();
  }, [purchaseId, navigate]);

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      {status === 'checking' && (
        <>
          <div className="text-3xl font-bold text-brand">Loading…</div>
        </>
      )}
      {status === 'completed' && (
        <>
          <div className="text-3xl font-bold text-emerald-600">You're enrolled!</div>
          <p className="mt-3 text-slate-600">Redirecting to your dashboard…</p>
        </>
      )}
      {status === 'guest' && (
        <>
          <div className="text-3xl font-bold text-emerald-600">You're enrolled!</div>
          <p className="mt-3 text-slate-600">
            You have been sent an email to complete the setup of your Learnable account. Please
            open the email and follow the instructions to verify your email address and create a
            password. (If you do not receive the email, be sure to check alternate email folders.)
          </p>
          <p className="mt-3 text-slate-600">
            Once your account is set up, you will have full access to the course, including the
            course roadmap and instructions for attending either in person or virtually, based on
            the specific course or the attendance option you selected.
          </p>
        </>
      )}
      {status === 'failed' && (
        <>
          <div className="text-3xl font-bold text-red-600">Payment failed</div>
          <p className="mt-3 text-slate-600">No charge was applied. Try again or use a different card.</p>
          <Link to="/library" className="inline-block mt-4 text-brand underline">Back to courses</Link>
        </>
      )}
      {status === 'unknown' && (
        <>
          <div className="text-3xl font-bold">Couldn't load purchase</div>
          <Link to="/dashboard" className="inline-block mt-4 text-brand underline">Go to dashboard</Link>
        </>
      )}
      {status === 'missing_id' && (
        <>
          <div className="text-3xl font-bold">No purchase reference</div>
          <Link to="/library" className="inline-block mt-4 text-brand underline">Back to courses</Link>
        </>
      )}
    </div>
  );
}
